import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/ui/Navbar";
import { Card, CardContent } from "../components/ui/card";
import SelectField from "../components/ui/SelectField";
import {
  AlertTriangle,
  Bot,
  CircleCheckBig,
  Clock3,
  LoaderPinwheel,
  ShieldAlert,
  Sparkles,
  Wrench,
  X,
  ListChecks,
  Lightbulb,
  RefreshCw,
  ClipboardList,
} from "lucide-react";

// ─── Mapas de estilos ────────────────────────────────────────────────────────

const estadoConfig = {
  Pendiente: {
    badge: "bg-amber-100 text-amber-800 border border-amber-200",
    dot: "bg-amber-400",
  },
  "En proceso": {
    badge: "bg-blue-100 text-blue-800 border border-blue-200",
    dot: "bg-blue-500",
  },
  Resuelto: {
    badge: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    dot: "bg-emerald-500",
  },
  Descartado: {
    badge: "bg-gray-100 text-gray-600 border border-gray-200",
    dot: "bg-gray-400",
  },
};

const riesgoConfig = {
  Bajo: {
    badge: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    border: "border-l-emerald-400",
    header: "bg-emerald-50",
    icon: "text-emerald-500",
    pill: "bg-emerald-500",
  },
  Medio: {
    badge: "bg-amber-100 text-amber-700 border border-amber-200",
    border: "border-l-amber-400",
    header: "bg-amber-50",
    icon: "text-amber-500",
    pill: "bg-amber-500",
  },
  Alto: {
    badge: "bg-orange-100 text-orange-700 border border-orange-200",
    border: "border-l-orange-500",
    header: "bg-orange-50",
    icon: "text-orange-500",
    pill: "bg-orange-500",
  },
  Crítico: {
    badge: "bg-red-100 text-red-700 border border-red-200",
    border: "border-l-red-500",
    header: "bg-red-50",
    icon: "text-red-500",
    pill: "bg-red-500",
  },
};

const estadosOptions = [
  { _id: "En proceso", name: "En proceso" },
  { _id: "Resuelto", name: "Resuelto" },
  { _id: "Descartado", name: "Descartado" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

// ─── Subcomponentes ──────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, color, sub }) => {
  const colorMap = {
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-100",
      icon: "text-blue-500 bg-blue-100",
      value: "text-blue-700",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-100",
      icon: "text-red-500 bg-red-100",
      value: "text-red-700",
    },
    amber: {
      bg: "bg-amber-50",
      border: "border-amber-100",
      icon: "text-amber-500 bg-amber-100",
      value: "text-amber-700",
    },
    emerald: {
      bg: "bg-emerald-50",
      border: "border-emerald-100",
      icon: "text-emerald-500 bg-emerald-100",
      value: "text-emerald-700",
    },
  };
  const c = colorMap[color] || colorMap.blue;
  return (
    <div
      className={`rounded-2xl border ${c.border} ${c.bg} p-4 flex items-center gap-4`}
    >
      <div className={`p-2.5 rounded-xl ${c.icon}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className={`text-2xl font-bold ${c.value}`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const Badge = ({ text, config }) => {
  const cfg = config || { badge: "bg-gray-100 text-gray-600" };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${cfg.badge}`}
    >
      {cfg.dot && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />}
      {text}
    </span>
  );
};

// ─── Página principal ────────────────────────────────────────────────────────

const PredictivoPage = () => {
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const planActual = (
    localStorage.getItem("planActual") || "FREE"
  ).toUpperCase();
  const planPermitido = ["PRO", "BLACK"].includes(planActual);

  const [equipos, setEquipos] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [predicciones, setPredicciones] = useState([]);
  const [stats, setStats] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("analisis");
  const [showForm, setShowForm] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroRiesgo, setFiltroRiesgo] = useState("");

  const [formGenerar, setFormGenerar] = useState({
    equipoId: "",
    contextoOperacion: "",
    sintomasReportados: "",
  });

  const [formGestion, setFormGestion] = useState({
    estado: "En proceso",
    descripcion: "",
    responsable: "",
    fechaCompromiso: todayIso(),
    notas: "",
  });

  const config = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const cargarStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/predictivo/stats`, config);
      setStats(data);
    } catch (_) {}
  }, [API, config]);

  const cargarInicial = useCallback(async () => {
    if (!planPermitido) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [equiposRes, predRes, respRes] = await Promise.all([
        axios.get(`${API}/equipos`, config),
        axios.get(`${API}/predictivo/predicciones`, config),
        axios.get(`${API}/users/resp`, config),
      ]);
      setEquipos(equiposRes.data || []);
      setResponsables(respRes.data || []);
      const dataPred = predRes.data || [];
      setPredicciones(dataPred);
      if (dataPred.length > 0) setSelectedId(dataPred[0]._id);
      await cargarStats();
    } catch (_) {
      toast.error("No se pudo cargar el módulo predictivo");
    } finally {
      setLoading(false);
    }
  }, [API, config, planPermitido, cargarStats]);

  useEffect(() => {
    if (!planPermitido) {
      toast.error(
        "El módulo predictivo solo está disponible para planes PRO o BLACK",
      );
      navigate("/dashboard");
      return;
    }
    cargarInicial();
  }, [cargarInicial, navigate, planPermitido]);

  const generarPrediccion = async (e) => {
    e.preventDefault();
    if (!formGenerar.equipoId)
      return toast.warning("Selecciona un equipo para analizar");
    try {
      setGenerando(true);
      const { data } = await axios.post(
        `${API}/predictivo/predicciones/generar`,
        formGenerar,
        config,
      );
      setPredicciones((prev) => [data, ...prev]);
      setSelectedId(data._id);
      setActiveTab("analisis");
      setShowForm(false);
      setFormGenerar({
        equipoId: "",
        contextoOperacion: "",
        sintomasReportados: "",
      });
      toast.success("Predicción generada con IA");
      await cargarStats();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "No se pudo generar la predicción",
      );
    } finally {
      setGenerando(false);
    }
  };

  const registrarGestion = async () => {
    if (!selectedId) return;
    if (!formGestion.descripcion.trim())
      return toast.warning("La descripción es obligatoria");
    try {
      setGuardando(true);
      const { data } = await axios.post(
        `${API}/predictivo/predicciones/${selectedId}/gestiones`,
        {
          ...formGestion,
          fechaCompromiso: formGestion.fechaCompromiso || null,
        },
        config,
      );
      setPredicciones((prev) =>
        prev.map((p) => (p._id === data._id ? data : p)),
      );
      setFormGestion({
        estado: data.estado === "Pendiente" ? "En proceso" : data.estado,
        descripcion: "",
        responsable: "",
        fechaCompromiso: todayIso(),
        notas: "",
      });
      toast.success("Gestión registrada");
      await cargarStats();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "No se pudo registrar la gestión",
      );
    } finally {
      setGuardando(false);
    }
  };

  // ── Predicciones filtradas ──────────────────────────────────────────────────
  const prediccionesFiltradas = useMemo(() => {
    return predicciones.filter((p) => {
      const texto = busqueda.toLowerCase();
      const matchBusqueda =
        !texto ||
        p.equipo?.serial?.toLowerCase().includes(texto) ||
        p.equipo?.marca?.name?.toLowerCase().includes(texto) ||
        p.equipo?.modelo?.name?.toLowerCase().includes(texto) ||
        p.resumen?.toLowerCase().includes(texto);
      const matchEstado = !filtroEstado || p.estado === filtroEstado;
      const matchRiesgo = !filtroRiesgo || p.riesgo === filtroRiesgo;
      return matchBusqueda && matchEstado && matchRiesgo;
    });
  }, [predicciones, busqueda, filtroEstado, filtroRiesgo]);

  // ── Stats calculadas ────────────────────────────────────────────────────────
  const totalPredicciones = stats?.total ?? predicciones.length;
  const criticos =
    stats?.porRiesgo?.find((r) => r._id === "Crítico")?.count ?? 0;
  const altos = stats?.porRiesgo?.find((r) => r._id === "Alto")?.count ?? 0;
  const pendientes =
    stats?.porEstado?.find((s) => s._id === "Pendiente")?.count ?? 0;
  const resueltos =
    stats?.porEstado?.find((s) => s._id === "Resuelto")?.count ?? 0;

  const selected = predicciones.find((p) => p._id === selectedId);
  const gestiones = selected?.gestiones || [];
  const bloqueada = ["Resuelto", "Descartado"].includes(selected?.estado);
  const rCfg = riesgoConfig[selected?.riesgo] || riesgoConfig.Bajo;
  const eCfg = estadoConfig[selected?.estado] || estadoConfig.Pendiente;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-6 pt-20 md:pt-28 space-y-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="p-2 rounded-xl bg-violet-100">
                <Bot className="h-5 w-5 text-violet-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">
                Mantenimiento Predictivo
              </h1>
            </div>
            <p className="text-sm text-gray-500 ml-11">
              Análisis inteligente de equipos biomédicos. Las predicciones se
              generan automáticamente para equipos con mantenimiento vencido.
            </p>
          </div>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors shadow-sm"
          >
            <Sparkles className="h-4 w-4" />
            {showForm ? "Cancelar" : "Nueva predicción"}
          </button>
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={ClipboardList}
            label="Total predicciones"
            value={totalPredicciones}
            color="blue"
          />
          <StatCard
            icon={ShieldAlert}
            label="Crítico / Alto"
            value={criticos + altos}
            color="red"
            sub={`${criticos} críticos, ${altos} altos`}
          />
          <StatCard
            icon={Clock3}
            label="Pendientes"
            value={pendientes}
            color="amber"
          />
          <StatCard
            icon={CircleCheckBig}
            label="Resueltas"
            value={resueltos}
            color="emerald"
          />
        </div>

        {/* ── Formulario de generación (colapsable) ── */}
        {showForm && (
          <Card className="border-violet-200 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-4 w-4 text-violet-500" />
                <h2 className="font-semibold text-gray-800">
                  Generar predicción con IA
                </h2>
              </div>
              <form
                onSubmit={generarPrediccion}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <SelectField
                  label="Equipo a analizar"
                  name="equipoId"
                  value={formGenerar.equipoId}
                  options={equipos}
                  placeholder="Seleccione un equipo..."
                  displayFn={(eq) =>
                    `${eq.serial} · ${eq.marca?.name || ""} ${eq.modelo?.name || ""}`
                  }
                  onChange={(e) =>
                    setFormGenerar((prev) => ({
                      ...prev,
                      [e.target.name]: e.target.value,
                    }))
                  }
                />
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Contexto operativo
                  </label>
                  <textarea
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"
                    rows={3}
                    placeholder="Ej: uso intensivo 24/7, ambientes húmedos, alta carga..."
                    value={formGenerar.contextoOperacion}
                    onChange={(e) =>
                      setFormGenerar((prev) => ({
                        ...prev,
                        contextoOperacion: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Síntomas reportados
                  </label>
                  <textarea
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"
                    rows={2}
                    placeholder="Ej: sobrecalentamiento ocasional, ruido inusual, reinicios..."
                    value={formGenerar.sintomasReportados}
                    onChange={(e) =>
                      setFormGenerar((prev) => ({
                        ...prev,
                        sintomasReportados: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="md:col-span-2 flex gap-3">
                  <button
                    type="submit"
                    disabled={generando}
                    className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
                  >
                    {generando ? (
                      <>
                        <LoaderPinwheel className="h-4 w-4 animate-spin" />{" "}
                        Analizando con IA...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" /> Generar predicción
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Contenido principal ── */}
        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-5">
          {/* ── Lista de predicciones ── */}
          <div className="lg:col-span-2">
            <Card className="shadow-sm">
              <CardContent className="p-4">
                {/* Barra de filtros */}
                <div className="space-y-2 mb-4">
                  <input
                    type="text"
                    placeholder="Buscar por serial, equipo o resumen..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                  <div className="flex gap-2">
                    <select
                      value={filtroEstado}
                      onChange={(e) => setFiltroEstado(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-xl px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    >
                      <option value="">Todos los estados</option>
                      <option value="Pendiente">Pendiente</option>
                      <option value="En proceso">En proceso</option>
                      <option value="Resuelto">Resuelto</option>
                      <option value="Descartado">Descartado</option>
                    </select>
                    <select
                      value={filtroRiesgo}
                      onChange={(e) => setFiltroRiesgo(e.target.value)}
                      className="flex-1 border border-gray-200 rounded-xl px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    >
                      <option value="">Todos los riesgos</option>
                      <option value="Bajo">Bajo</option>
                      <option value="Medio">Medio</option>
                      <option value="Alto">Alto</option>
                      <option value="Crítico">Crítico</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-700">
                    {prediccionesFiltradas.length} predicciones
                  </h2>
                  {(filtroEstado || filtroRiesgo || busqueda) && (
                    <button
                      onClick={() => {
                        setFiltroEstado("");
                        setFiltroRiesgo("");
                        setBusqueda("");
                      }}
                      className="text-xs text-violet-600 hover:underline flex items-center gap-1"
                    >
                      <X className="h-3 w-3" /> Limpiar filtros
                    </button>
                  )}
                </div>

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
                    <LoaderPinwheel className="h-8 w-8 animate-spin text-violet-400" />
                    <p className="text-sm">Cargando predicciones...</p>
                  </div>
                ) : prediccionesFiltradas.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
                    <Bot className="h-10 w-10 text-gray-300" />
                    <p className="text-sm font-medium">Sin predicciones</p>
                    <p className="text-xs text-center">
                      Genera una predicción para un equipo o espera el análisis
                      automático diario
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-auto pr-1">
                    {prediccionesFiltradas.map((pred) => {
                      const rc = riesgoConfig[pred.riesgo] || riesgoConfig.Bajo;
                      const ec =
                        estadoConfig[pred.estado] || estadoConfig.Pendiente;
                      const isSelected = pred._id === selectedId;
                      return (
                        <button
                          key={pred._id}
                          onClick={() => {
                            setSelectedId(pred._id);
                            setActiveTab("analisis");
                          }}
                          className={`w-full text-left rounded-xl border-l-4 border border-r border-t border-b transition-all
                            ${rc.border}
                            ${
                              isSelected
                                ? "bg-violet-50 border-r-violet-200 border-t-violet-200 border-b-violet-200 shadow-sm"
                                : "bg-white border-r-gray-100 border-t-gray-100 border-b-gray-100 hover:bg-gray-50"
                            }`}
                        >
                          <div className="p-3">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div>
                                <p className="text-sm font-semibold text-gray-800">
                                  {pred.equipo?.serial || "Sin serial"}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {pred.equipo?.marca?.name || ""}{" "}
                                  {pred.equipo?.modelo?.name || ""}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <Badge text={pred.riesgo} config={rc} />
                                <Badge text={pred.estado} config={ec} />
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                              {pred.resumen}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-400">
                                {new Date(pred.createdAt).toLocaleDateString(
                                  "es-CO",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                              </span>
                              {pred.gestiones?.length > 0 && (
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                  <ListChecks className="h-3 w-3" />
                                  {pred.gestiones.length} acciones
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Panel de detalle ── */}
          <div
            className={`
              lg:col-span-3
              fixed lg:static inset-0 lg:inset-auto
              bg-white lg:bg-transparent
              z-50 lg:z-auto
              transition-transform duration-300
              ${selected ? "translate-x-0" : "translate-x-full lg:translate-x-0"}
            `}
          >
            <Card className="h-full shadow-sm">
              {!selected ? (
                <CardContent className="flex flex-col items-center justify-center h-full py-20 gap-3 text-gray-400">
                  <Bot className="h-14 w-14 text-gray-200" />
                  <p className="font-medium text-gray-500">
                    Selecciona una predicción
                  </p>
                  <p className="text-sm text-center max-w-xs">
                    Elige una predicción de la lista para ver el análisis
                    completo y gestionar las acciones correctivas
                  </p>
                </CardContent>
              ) : (
                <CardContent className="p-0 h-full flex flex-col overflow-hidden">
                  {/* Header del equipo */}
                  <div className={`px-5 py-4 ${rCfg.header} border-b`}>
                    {/* Botón volver móvil */}
                    <div className="lg:hidden flex justify-end mb-2">
                      <button
                        onClick={() => setSelectedId(null)}
                        className="text-xs text-gray-500 flex items-center gap-1"
                      >
                        <X className="h-3.5 w-3.5" /> Cerrar
                      </button>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-bold text-lg text-gray-800`}>
                            {selected.equipo?.serial || "Sin serial"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {selected.equipo?.marca?.name || ""}{" "}
                          {selected.equipo?.modelo?.name || ""}
                          {selected.equipo?.categoria?.name && (
                            <span className="text-gray-400">
                              {" "}
                              · {selected.equipo.categoria.name}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Generado:{" "}
                          {new Date(selected.createdAt).toLocaleDateString(
                            "es-CO",
                            {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            },
                          )}
                          {selected.generadoPor?.name &&
                            ` · ${selected.generadoPor.name}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <Badge text={selected.riesgo} config={rCfg} />
                        <Badge text={selected.estado} config={eCfg} />
                        {bloqueada && selected.fechaCierre && (
                          <span className="text-xs text-gray-400">
                            Cerrado:{" "}
                            {new Date(selected.fechaCierre).toLocaleDateString(
                              "es-CO",
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex border-b bg-white">
                    {[
                      { id: "analisis", label: "Análisis IA", icon: Lightbulb },
                      {
                        id: "gestiones",
                        label: `Acciones (${gestiones.length})`,
                        icon: Wrench,
                      },
                    ].map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        onClick={() => setActiveTab(id)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors border-b-2
                          ${
                            activeTab === id
                              ? "border-violet-500 text-violet-600 bg-violet-50"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Contenido del tab */}
                  <div className="flex-1 overflow-auto">
                    {/* TAB: Análisis */}
                    {activeTab === "analisis" && (
                      <div className="p-5 space-y-5">
                        {/* Resumen */}
                        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Bot className="h-4 w-4 text-violet-500" />
                            <p className="text-sm font-semibold text-gray-700">
                              Resumen IA
                            </p>
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {selected.resumen || "Sin resumen disponible"}
                          </p>
                        </div>

                        {/* Posibles fallas */}
                        {selected.probablesFallas?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <AlertTriangle
                                className={`h-4 w-4 ${rCfg.icon}`}
                              />
                              <p className="text-sm font-semibold text-gray-700">
                                Posibles fallas detectadas
                              </p>
                            </div>
                            <ul className="space-y-2">
                              {selected.probablesFallas.map((f, i) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-2 text-sm text-gray-600"
                                >
                                  <span
                                    className={`mt-1 w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-white text-xs font-bold ${rCfg.pill}`}
                                  >
                                    {i + 1}
                                  </span>
                                  {f}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Recomendaciones */}
                        {selected.recomendaciones?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <Lightbulb className="h-4 w-4 text-amber-500" />
                              <p className="text-sm font-semibold text-gray-700">
                                Recomendaciones
                              </p>
                            </div>
                            <ul className="space-y-2">
                              {selected.recomendaciones.map((r, i) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-2 text-sm text-gray-600"
                                >
                                  <span className="mt-1 w-5 h-5 shrink-0 rounded-full flex items-center justify-center bg-amber-100 text-amber-700 text-xs font-bold">
                                    {i + 1}
                                  </span>
                                  {r}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Actualizaciones sugeridas */}
                        {selected.actualizacionesSugeridas?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <RefreshCw className="h-4 w-4 text-blue-500" />
                              <p className="text-sm font-semibold text-gray-700">
                                Actualizaciones sugeridas
                              </p>
                            </div>
                            <ul className="space-y-2">
                              {selected.actualizacionesSugeridas.map((a, i) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-2 text-sm text-gray-600"
                                >
                                  <span className="mt-1 w-5 h-5 shrink-0 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 text-xs font-bold">
                                    {i + 1}
                                  </span>
                                  {a}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Contexto original */}
                        {(selected.contextoOperacion ||
                          selected.sintomasReportados) && (
                          <details className="rounded-xl border border-gray-100">
                            <summary className="px-4 py-3 text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">
                              Ver contexto del análisis
                            </summary>
                            <div className="px-4 pb-4 space-y-2">
                              {selected.contextoOperacion && (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 mb-1">
                                    Contexto operativo
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {selected.contextoOperacion}
                                  </p>
                                </div>
                              )}
                              {selected.sintomasReportados && (
                                <div>
                                  <p className="text-xs font-medium text-gray-500 mb-1">
                                    Síntomas reportados
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {selected.sintomasReportados}
                                  </p>
                                </div>
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    )}

                    {/* TAB: Gestiones */}
                    {activeTab === "gestiones" && (
                      <div className="p-5 space-y-4">
                        {/* Historial de acciones */}
                        {gestiones.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold text-gray-700 mb-3">
                              Historial de acciones
                            </p>
                            <div className="space-y-3 max-h-64 overflow-auto pr-1">
                              {gestiones
                                .slice()
                                .reverse()
                                .map((g, i) => {
                                  const gc =
                                    estadoConfig[g.estado] ||
                                    estadoConfig.Pendiente;
                                  return (
                                    <div
                                      key={g._id}
                                      className="rounded-xl border border-gray-100 bg-gray-50 p-3"
                                    >
                                      <div className="flex items-center justify-between mb-1.5">
                                        <Badge text={g.estado} config={gc} />
                                        <span className="text-xs text-gray-400">
                                          {new Date(
                                            g.fechaRegistro,
                                          ).toLocaleDateString("es-CO", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          })}
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-700">
                                        {g.descripcion}
                                      </p>
                                      {g.notas && (
                                        <p className="text-xs text-gray-500 mt-1 italic">
                                          {g.notas}
                                        </p>
                                      )}
                                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                        {g.registradoPor?.name && (
                                          <span>
                                            Por: {g.registradoPor.name}
                                          </span>
                                        )}
                                        {g.responsable && (
                                          <span>
                                            Responsable: {g.responsable}
                                          </span>
                                        )}
                                        {g.fechaCompromiso && (
                                          <span>
                                            Compromiso:{" "}
                                            {new Date(
                                              g.fechaCompromiso,
                                            ).toLocaleDateString("es-CO")}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Formulario de nueva gestión */}
                        {bloqueada ? (
                          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-center">
                            <CircleCheckBig
                              className={`h-8 w-8 mx-auto mb-2 ${selected.estado === "Resuelto" ? "text-emerald-400" : "text-gray-400"}`}
                            />
                            <p className="text-sm font-medium text-gray-600">
                              Predicción {selected.estado.toLowerCase()}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              No se pueden registrar más acciones en una
                              predicción cerrada
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-sm font-semibold text-gray-700">
                              Registrar nueva acción
                            </p>

                            <SelectField
                              label="Estado de la predicción"
                              name="estado"
                              value={formGestion.estado}
                              options={estadosOptions}
                              valueKey="_id"
                              onChange={(e) =>
                                setFormGestion((prev) => ({
                                  ...prev,
                                  estado: e.target.value,
                                }))
                              }
                            />

                            <div>
                              <label className="text-sm font-medium text-gray-700 block mb-1">
                                Descripción{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                rows={3}
                                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"
                                placeholder="Describe la acción tomada..."
                                value={formGestion.descripcion}
                                onChange={(e) =>
                                  setFormGestion((prev) => ({
                                    ...prev,
                                    descripcion: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            <SelectField
                              label="Responsable"
                              name="responsable"
                              value={formGestion.responsable}
                              options={responsables}
                              valueKey="name"
                              placeholder="Seleccione un responsable..."
                              displayFn={(u) =>
                                `${u.name}${u.email ? ` (${u.email})` : ""}`
                              }
                              onChange={(e) =>
                                setFormGestion((prev) => ({
                                  ...prev,
                                  responsable: e.target.value,
                                }))
                              }
                            />

                            <div>
                              <label className="text-sm font-medium text-gray-700 block mb-1">
                                Fecha de compromiso
                              </label>
                              <input
                                type="date"
                                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                                value={formGestion.fechaCompromiso}
                                onChange={(e) =>
                                  setFormGestion((prev) => ({
                                    ...prev,
                                    fechaCompromiso: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            <div>
                              <label className="text-sm font-medium text-gray-700 block mb-1">
                                Notas adicionales
                              </label>
                              <textarea
                                rows={2}
                                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"
                                placeholder="Observaciones, materiales usados, etc."
                                value={formGestion.notas}
                                onChange={(e) =>
                                  setFormGestion((prev) => ({
                                    ...prev,
                                    notas: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            <div className="sticky bottom-0 bg-white pt-2 pb-1">
                              <button
                                onClick={registrarGestion}
                                disabled={guardando}
                                className="w-full inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
                              >
                                {guardando ? (
                                  <>
                                    <LoaderPinwheel className="h-4 w-4 animate-spin" />{" "}
                                    Guardando...
                                  </>
                                ) : (
                                  <>
                                    <ListChecks className="h-4 w-4" /> Registrar
                                    acción
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictivoPage;
